import { useState } from "react";
import { Button, Chip, Tab, Tabs, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Tooltip } from "@nextui-org/react";
import RootAdmin from "../../../components/layouts/RootAdmin";
import Draft from "../../../components/fragments/admin/recomendation/Draft/Draft";
import Information from "../../../components/fragments/admin/recomendation/Information";
import { useParams } from "react-router-dom";
import { DocumentIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import HeaderPage from "../../../components/elements/HeaderPage";
import useSWR from "swr";
import toast from "react-hot-toast";
import { getFetcher, getPdfUrl, patchFetcher } from "../../../services/api";
import StatusRekomendasi from "../../../enums/statusRekomendasi";

export default function DetailPage() {
  const { id } = useParams();
  const { data, isLoading, mutate } = useSWR(`/api/rekom/permohonan/${id}`, getFetcher);
  const [isDraftLoading, setDraftLoading] = useState(false);

  // State untuk modal konfirmasi proses
  const [isOpenProcessModal, setIsOpenProcessModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const disableTelaahAndDraftSK = ["PENDING", "VALIDASI_PEMOHONAN_DITOLAK", "VALIDASI_PEMOHONAN", "PEMOHONAN_DI_PROSES", "DRAFT_PERMOHONAN", StatusRekomendasi.DRAFT_PERMOHONAN, StatusRekomendasi.VALIDASI_PEMOHONAN].includes(data?.status);

  // Cek apakah permohonan sudah diproses
  const isProcessed = data?.isProcessed;

  let tabs = [
    {
      id: "informasi",
      label: "Informasi",
      content: <Information data={data} isLoading={isLoading} mutate={mutate} />,
    },
    {
      id: "draft",
      label: "Draft SK",
      content: <Draft data={data} mutate={mutate} />,
      disabled: disableTelaahAndDraftSK,
    },
  ];

  const openPdfDirectly = async (endpoint, setLoadingState) => {
    try {
      setLoadingState(true);
      await getPdfUrl(endpoint);
    } catch (error) {
      toast.error("Terapkan nomor surat untuk SK rekomendasi pengangkutan");
    } finally {
      setLoadingState(false);
    }
  };

  // Function untuk handle proses permohonan
  const handleProcessPermohonan = async () => {
    setIsProcessing(true);
    try {
      console.log("Processing permohonan ID:", id);
      await patchFetcher(`/api/rekom/permohonan/process`, id, {});

      toast.success("Permohonan berhasil diproses");
      mutate(); // Refresh data
      closeProcessModal();
    } catch (error) {
      const msg = error?.response?.data?.message?.error || error?.response?.data?.message || "Gagal memproses permohonan";
      console.error("Process error:", error);
      toast.error(msg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Function untuk membuka modal konfirmasi
  const openProcessModal = () => {
    setIsOpenProcessModal(true);
  };

  // Function untuk menutup modal konfirmasi
  const closeProcessModal = () => {
    setIsOpenProcessModal(false);
  };

  // Function untuk konfirmasi proses
  const confirmProcess = () => {
    handleProcessPermohonan();
  };

  // Render button berdasarkan status
  const renderActionButton = () => {
    if (!isProcessed && !disableTelaahAndDraftSK) {
      // Jika belum diproses, tampilkan tombol proses
      return (
        <Tooltip content="Proses permohonan ini">
          <Button startContent={<ArrowPathIcon className="size-4" />} size="sm" color="warning" isDisabled={isProcessing} isLoading={isProcessing} onPress={openProcessModal}>
            Proses Permohonan
          </Button>
        </Tooltip>
      );
    } else if (!disableTelaahAndDraftSK) {
      // Jika sudah diproses, tampilkan tombol view draft SK
      return (
        <Tooltip content="Lihat draft SK">
          <Button startContent={<DocumentIcon className="size-4" />} size="sm" color="primary" isDisabled={isDraftLoading} isLoading={isDraftLoading} onPress={() => openPdfDirectly(`/api/pdf/generateRekomendasiB3/${id}`, setDraftLoading)}>
            View Draft SK
          </Button>
        </Tooltip>
      );
    }
    return null;
  };

  return (
    <RootAdmin>
      <HeaderPage
        title={data?.kodePermohonan}
        subtitle="Kode Registrasi"
        action={
          <div className="flex flex-col gap-2">
            <div className="flex justify-end">
              <Chip size="sm" variant="bordered" color="primary">
                {data?.status}
              </Chip>
            </div>
            <div className="w-full flex justify-end">{renderActionButton()}</div>
          </div>
        }
      />
      <div className="flex w-full flex-col pt-5">
        <Tabs color="primary" radius="sm" size="md" variant="bordered" aria-label="form">
          {tabs.map((tab) => (
            <Tab key={tab.id} title={tab.label} disabled={tab.disabled}>
              {tab.content}
            </Tab>
          ))}
        </Tabs>
      </div>

      {/* Modal Konfirmasi Proses */}
      <Modal isOpen={isOpenProcessModal} onOpenChange={setIsOpenProcessModal} onClose={closeProcessModal} size="md" backdrop="blur">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Konfirmasi Proses</ModalHeader>
              <ModalBody>
                <div className="flex flex-col gap-2">
                  <p className="text-ink-700">Apakah Anda yakin ingin memproses permohonan ini?</p>
                  <p className="text-sm text-ink-500">
                    Kode Permohonan: <strong>{data?.kodePermohonan}</strong>
                  </p>
                  <p className="text-sm text-ink-500">Tindakan ini akan mengubah status permohonan dan tidak dapat dibatalkan.</p>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onPress={onClose} isDisabled={isProcessing}>
                  Batal
                </Button>
                <Button color="warning" onPress={confirmProcess} isLoading={isProcessing} isDisabled={isProcessing} startContent={!isProcessing && <ArrowPathIcon className="size-4" />}>
                  {isProcessing ? "Memproses..." : "Ya, Proses"}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </RootAdmin>
  );
}
